/** DRF returns 400 as a map of field name to messages (plus `detail`/`non_field_errors`). */
export type FieldErrorMap = Record<string, string[]>;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** 400 — keeps the field map so a form can put each message on its own input. */
export class ValidationError extends ApiError {
  constructor(
    readonly fields: FieldErrorMap,
    message: string,
  ) {
    super(message, 400);
    this.name = "ValidationError";
  }

  first(field: string): string | undefined {
    return this.fields[field]?.[0];
  }
}

/** 401/403 — bad, missing, or expired initData, or an account not on the allowlist. */
export class AuthError extends ApiError {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = "AuthError";
  }
}

/** 409 — a PROTECT violation: the row is still referenced. */
export class ReferencedError extends ApiError {
  constructor(
    message: string,
    readonly referencedBy: number,
  ) {
    super(message, 409);
    this.name = "ReferencedError";
  }
}

/** 429 — throttled. `retryAfter` is in seconds. */
export class ThrottleError extends ApiError {
  constructor(
    message: string,
    readonly retryAfter: number,
  ) {
    super(message, 429);
    this.name = "ThrottleError";
  }
}

/** The request never reached the server. */
export class NetworkError extends ApiError {
  constructor(message: string) {
    super(message, 0);
    this.name = "NetworkError";
  }
}

/** Flattens a DRF error body into readable lines, skipping the field names. */
export function messagesOf(error: unknown): string[] {
  if (error instanceof ValidationError) {
    return Object.values(error.fields).flat();
  }
  if (error instanceof Error) return [error.message];
  return [String(error)];
}
